import React, { useEffect, useRef, useState } from 'react';
import { FiSearch, FiX, FiSave, FiCheck, FiArrowUp, FiArrowDown, FiChevronsUp, FiChevronsDown, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import { adminApi } from '../adminApi';
import { showAlert } from '../utils/dialog';

function AdminNewArrivals() {
  const [allProducts, setAllProducts] = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search,  setSearch]  = useState('');
  const [saving,  setSaving]  = useState(false);
  const [loading, setLoading] = useState(true);
  const [saved,   setSaved]   = useState(false);
  const [selOverIdx, setSelOverIdx] = useState(null);
  const [reorderSelected, setReorderSelected] = useState(new Set());
  const [discountOpen,  setDiscountOpen]  = useState(new Set());
  const [discountEdits, setDiscountEdits] = useState({});
  const selDragIdx = useRef(null);

  const loadProducts = () => {
    adminApi.getProducts().then(r => {
      setAllProducts(r.data || []);
      setDiscountOpen(new Set((r.data || []).filter(x => x.discount_price && x.discount_active !== false).map(x => x.id)));
    }).catch(() => {});
  };

  useEffect(() => {
    Promise.all([adminApi.getProducts(), adminApi.getCategories(), adminApi.getStoreSettings()])
      .then(([prodRes, catRes, settingsRes]) => {
        setAllProducts(prodRes.data || []);
        setCategories(catRes.data || []);
        setDiscountOpen(new Set((prodRes.data || []).filter(x => x.discount_price && x.discount_active !== false).map(x => x.id)));
        const raw = settingsRes.data?.settings?.newArrivals?.products || [];
        const ids = raw.map(p => typeof p === 'object' ? p.id : p).filter(Boolean);
        setSelectedIds(ids);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const catName = (catId) => {
    const chain = [];
    let c = categories.find(c => c.id === catId);
    while (c) { chain.unshift(c.name); c = c.parent_id ? categories.find(p => p.id === c.parent_id) : null; }
    return chain.length ? chain.join(' › ') : '—';
  };

  const toggle = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleDiscount = (id) => {
    const p = allProducts.find(x => x.id === id);
    if (!p) return;
    const isOn = discountOpen.has(id);
    setDiscountOpen(prev => { const n = new Set(prev); isOn ? n.delete(id) : n.add(id); return n; });
    setDiscountEdits(prev => { const n = { ...prev }; delete n[id]; return n; });
    if (isOn) {
      setAllProducts(prev => prev.map(x => x.id === id ? { ...x, discount_active: false } : x));
      adminApi.updateProduct(id, { discount_active: false }).catch(() => loadProducts());
    } else {
      setAllProducts(prev => prev.map(x => x.id === id ? { ...x, discount_active: true } : x));
      adminApi.updateProduct(id, { discount_active: true }).catch(() => loadProducts());
    }
  };

  const saveDiscountPrice = async (id) => {
    const p = allProducts.find(x => x.id === id);
    if (!p) return;
    const val = discountEdits[id];
    if (val === undefined || val === '') { setDiscountEdits(prev => { const n = { ...prev }; delete n[id]; return n; }); return; }
    const discount_price = parseFloat(val);
    if (isNaN(discount_price) || discount_price <= 0 || discount_price >= p.price) { setDiscountEdits(prev => { const n = { ...prev }; delete n[id]; return n; }); return; }
    setAllProducts(prev => prev.map(x => x.id === id ? { ...x, discount_price, discount_active: true } : x));
    setDiscountEdits(prev => { const n = { ...prev }; delete n[id]; return n; });
    setDiscountOpen(prev => { const n = new Set(prev); n.add(id); return n; });
    adminApi.updateProduct(id, { discount_price, discount_active: true }).catch(() => loadProducts());
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const settingsRes = await adminApi.getStoreSettings();
      const current = settingsRes.data?.settings || {};
      await adminApi.saveStoreSettings({
        settings: { ...current, newArrivals: { ...(current.newArrivals || {}), products: selectedIds } },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { showAlert('Save failed'); }
    finally { setSaving(false); }
  };

  const filtered = allProducts.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.barcode || '').toLowerCase().includes(search.toLowerCase())
  );

  const selDragStart = (idx) => { selDragIdx.current = idx; };
  const selDragOver  = (e, idx) => { e.preventDefault(); setSelOverIdx(idx); };
  const selDrop      = (idx) => {
    if (selDragIdx.current === null || selDragIdx.current === idx) { setSelOverIdx(null); return; }
    const a = [...selectedIds];
    const [moved] = a.splice(selDragIdx.current, 1);
    a.splice(idx, 0, moved);
    setSelectedIds(a);
    selDragIdx.current = null;
    setSelOverIdx(null);
  };

  const toggleReorder = (id) => {
    setReorderSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const toggleReorderAll = () => {
    if (reorderSelected.size === selectedIds.length) setReorderSelected(new Set());
    else setReorderSelected(new Set(selectedIds));
  };

  const moveGroup = (dir) => {
    if (reorderSelected.size === 0) return;
    const arr = [...selectedIds];
    const selIdxs = arr.map((id, i) => reorderSelected.has(id) ? i : -1).filter(i => i >= 0);
    if (dir === 'top') { setSelectedIds([...arr.filter(id => reorderSelected.has(id)), ...arr.filter(id => !reorderSelected.has(id))]); }
    else if (dir === 'bottom') { setSelectedIds([...arr.filter(id => !reorderSelected.has(id)), ...arr.filter(id => reorderSelected.has(id))]); }
    else if (dir === 'up') {
      if (selIdxs[0] === 0) return;
      const next = [...arr]; selIdxs.forEach(i => { [next[i-1], next[i]] = [next[i], next[i-1]]; }); setSelectedIds(next);
    } else if (dir === 'down') {
      if (selIdxs[selIdxs.length-1] === arr.length-1) return;
      const next = [...arr]; [...selIdxs].reverse().forEach(i => { [next[i+1], next[i]] = [next[i], next[i+1]]; }); setSelectedIds(next);
    }
  };

  if (loading) return <div className="admin-loading"><div className="admin-spinner" /> Loading…</div>;

  return (
    <div>
      <div className="admin-card" style={{ marginBottom: 16 }}>
        <div className="admin-card-header">
          <h3>New Arrivals — {selectedIds.length} selected</h3>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div className="admin-search">
              <FiSearch size={14} color="var(--text-muted)" />
              <input placeholder="Search by name or barcode…" value={search} onChange={e => setSearch(e.target.value)} />
              {search && <button onClick={() => setSearch('')} style={{ background:'none', border:'none', cursor:'pointer', padding:2, display:'flex', color:'var(--text-muted)' }}><FiX size={13}/></button>}
            </div>
            <button className="btn-admin btn-admin-primary" onClick={handleSave} disabled={saving}>
              <FiSave size={14} /> {saving ? 'Saving…' : saved ? 'Saved!' : 'Save Changes'}
            </button>
          </div>
        </div>

        {filtered.length === 0 ? <div className="admin-empty">No products found</div> : (
          <div className="admin-table-wrap" style={{ maxHeight: 480, overflowY: 'auto' }}>
            <table className="admin-table">
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
                <tr>
                  <th style={{ width: 40 }}></th>
                  <th>Product</th>
                  <th>Barcode / Article</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th style={{ width: 70 }}>Added</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => {
                  const isSel = selectedIds.includes(p.id);
                  return (
                    <tr key={p.id} style={{ background: isSel ? '#f0fdf4' : 'transparent', opacity: p.is_active === false ? 0.55 : 1 }}>
                      <td onClick={() => toggle(p.id)} style={{ cursor: 'pointer' }}>
                        <div style={{ width:22, height:22, borderRadius:6, border: isSel ? '2px solid #22c55e' : '2px solid #e2e8f0', background: isSel ? '#22c55e' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center' }}>
                          {isSel && <FiCheck size={13} color="#fff" strokeWidth={3} />}
                        </div>
                      </td>
                      <td onClick={() => toggle(p.id)} style={{ cursor: 'pointer' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <img src={p.image_url || 'https://placehold.co/40x40'} alt={p.name} className="product-thumb" />
                          <span className="bold" style={{ fontSize:'.875rem' }}>{p.name}</span>
                          {p.is_active === false && <span style={{ fontSize:'.68rem', fontWeight:700, color:'#ef4444', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:4, padding:'1px 6px' }}>Inactive</span>}
                        </div>
                      </td>
                      <td style={{ fontSize:'.8125rem', fontFamily:'monospace', color: p.barcode ? 'var(--text)' : 'var(--text-muted)' }}>{p.barcode || '—'}</td>
                      <td style={{ fontSize:'.8125rem' }}>{catName(p.category_id)}</td>
                      <td style={{ minWidth: 160 }}>
                        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                          <span className="bold" style={{ fontSize:'.8rem', color:'#64748b' }}>Rs {p.price.toFixed(0)}</span>
                          <div style={{ display:'flex', alignItems:'center', gap:5, width:150 }}>
                            <button
                              onClick={() => toggleDiscount(p.id)}
                              style={{ fontSize:'.65rem', fontWeight:700, padding:'1px 0', borderRadius:4, cursor:'pointer', width:36, textAlign:'center', flexShrink:0, background: discountOpen.has(p.id) ? '#dcfce7' : '#f1f5f9', color: discountOpen.has(p.id) ? '#16a34a' : '#94a3b8', border:`1px solid ${discountOpen.has(p.id) ? '#86efac' : '#e2e8f0'}` }}
                            >
                              {discountOpen.has(p.id) ? 'ON' : 'OFF'}
                            </button>
                            {discountOpen.has(p.id) ? (
                              <input
                                type="number"
                                value={discountEdits[p.id] !== undefined ? discountEdits[p.id] : (p.discount_price || '')}
                                onChange={e => setDiscountEdits(prev => ({ ...prev, [p.id]: e.target.value }))}
                                onBlur={() => saveDiscountPrice(p.id)}
                                onKeyDown={e => e.key === 'Enter' && saveDiscountPrice(p.id)}
                                onClick={e => e.stopPropagation()}
                                style={{ width:80, padding:'2px 6px', border:'1.5px solid #86efac', borderRadius:4, fontSize:'.8rem', fontFamily:'inherit', outline:'none', color:'#16a34a', fontWeight:700 }}
                                placeholder="Price"
                              />
                            ) : p.discount_price ? (
                              <span style={{ fontSize:'.8rem', color:'#94a3b8', fontWeight:600 }}>Rs {p.discount_price.toFixed(0)}</span>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td><span className={`badge ${p.stock>10?'badge-success':p.stock>0?'badge-warning':'badge-error'}`}>{p.stock}</span></td>
                      <td>
                        <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:'.78rem', fontWeight:600, padding:'3px 10px', borderRadius:20, background: p.is_active!==false ? '#dcfce7' : '#f1f5f9', color: p.is_active!==false ? '#16a34a' : '#94a3b8', border: `1px solid ${p.is_active!==false ? '#86efac' : '#e2e8f0'}` }}>
                          {p.is_active !== false ? <FiToggleRight size={13}/> : <FiToggleLeft size={13}/>}
                          {p.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        {isSel ? <span style={{ color:'#22c55e', fontWeight:700, fontSize:'.8rem' }}>✓ Added</span>
                               : <span style={{ color:'var(--text-muted)', fontSize:'.8rem' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedIds.length > 0 && (
        <div className="admin-card" style={{ marginTop: 16 }}>
          <div className="admin-card-header" style={{ paddingBottom: 8 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div onClick={toggleReorderAll} style={{ width:18, height:18, borderRadius:4, cursor:'pointer', flexShrink:0, border: reorderSelected.size===selectedIds.length && selectedIds.length>0 ? '2px solid #6366f1' : '2px solid #cbd5e1', background: reorderSelected.size===selectedIds.length && selectedIds.length>0 ? '#6366f1' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center' }}>
                {reorderSelected.size===selectedIds.length && selectedIds.length>0 && <FiCheck size={11} color="#fff" strokeWidth={3}/>}
              </div>
              <h3 style={{ fontSize:'.875rem' }}>Selected ({selectedIds.length}) — drag to reorder</h3>
            </div>
            {reorderSelected.size > 0 && (
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ fontSize:'.72rem', color:'var(--text-muted)', marginRight:4 }}>{reorderSelected.size} selected</span>
                {[['top',<FiChevronsUp size={13}/>,'Top'],['up',<FiArrowUp size={13}/>,'Up'],['down',<FiArrowDown size={13}/>,'Down'],['bottom',<FiChevronsDown size={13}/>,'Bottom']].map(([dir,icon,lbl]) => (
                  <button key={dir} onClick={() => moveGroup(dir)} style={{ display:'flex', alignItems:'center', gap:3, padding:'4px 8px', border:'1.5px solid #e2e8f0', borderRadius:6, background:'#fff', cursor:'pointer', fontSize:'.72rem', fontWeight:600 }}>{icon} {lbl}</button>
                ))}
              </div>
            )}
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6, padding:'4px 0 8px' }}>
            {selectedIds.map((id, idx) => {
              const p = allProducts.find(x => x.id === id);
              if (!p) return null;
              const isReorderSel = reorderSelected.has(id);
              return (
                <div key={id} draggable onDragStart={() => selDragStart(idx)} onDragOver={e => selDragOver(e, idx)} onDrop={() => selDrop(idx)} onDragEnd={() => setSelOverIdx(null)}
                  style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 12px', background: isReorderSel ? '#eef2ff' : selOverIdx===idx ? '#eff6ff' : '#f8fafc', border:`1.5px solid ${isReorderSel ? '#6366f1' : selOverIdx===idx ? '#3b82f6' : '#e2e8f0'}`, borderRadius:8, cursor:'grab', opacity: p.is_active===false ? 0.45 : 1 }}
                >
                  <div onClick={e => { e.stopPropagation(); toggleReorder(id); }} style={{ width:18, height:18, borderRadius:4, cursor:'pointer', flexShrink:0, border: isReorderSel ? '2px solid #6366f1' : '2px solid #cbd5e1', background: isReorderSel ? '#6366f1' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {isReorderSel && <FiCheck size={11} color="#fff" strokeWidth={3}/>}
                  </div>
                  <span style={{ color:'#94a3b8', fontSize:'1rem', userSelect:'none' }}>⠿</span>
                  <span style={{ fontSize:'.72rem', fontWeight:700, color:'#94a3b8', minWidth:18 }}>{idx+1}</span>
                  <img src={p.image_url || 'https://placehold.co/32x32'} alt={p.name} style={{ width:32, height:32, objectFit:'contain', borderRadius:4, border:'1px solid #e2e8f0' }}/>
                  <span style={{ flex:1, fontSize:'.8rem', fontWeight:600 }}>{p.name}</span>
                  {p.is_active === false && <span style={{ fontSize:'.68rem', fontWeight:700, color:'#ef4444', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:4, padding:'1px 6px' }}>Inactive</span>}
                  <span style={{ fontSize:'.75rem', color:'var(--text-muted)' }}>Rs {(p.discount_price || p.price).toFixed(0)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminNewArrivals;
